import React, { useState, useMemo } from "react";
import TableCard from "../../shared/TableCard/TableCard";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { AutoComplete } from "primereact/autocomplete";
import api from "../../services/api";
import Constants from "../../services/constant";
import { getGasFeesCurrencyList, updateGasFees, deleteGasFees } from "../../redux/actions/gasFeesAction";
import { useDispatch } from "react-redux";
import { types } from "../../redux/types/types";
import { CustomDropdown } from "../../shared/Input/CustomDropdown";

const DEFAULT_NETWORKS = [
    { label: "Tron", value: "tron" },
    { label: "Ethereum", value: "ethereum" },
    { label: "Polygon", value: "polygon" },
];

const STABLECOINS = ["USDT", "USDC", "USD"];

const adjustRate = (rate, firstCurrency, secondCurrency) => {
    if (!rate) return rate;
    const num = Number(rate);
    if (firstCurrency === "CAD" && STABLECOINS.includes(secondCurrency) && num > 1) {
        return 1 / num;
    }
    return num;
};

const DEFAULT_CURRENCIES = ["USDT", "USDC", "CAD", "USD"];

export default function GasFeesTable({ heading, gasFees }) {
    const [loading, setLoading] = useState(false);
    const [addDialogVisible, setAddDialogVisible] = useState(false);
    const [newFee, setNewFee] = useState({
        firstCurrency: "USDT",
        secondCurrency: "CAD",
        network: "tron",
        exchangePricePerUnit: "",
        fees: "5.00",
        transactionFee: "2.50",
        selectedExchangeRate: "User",
        marketSpreadPercent: 0,
    });

    const [editingRows, setEditingRows] = useState({});

    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const [deleteError, setDeleteError] = useState("");

    const [firstCurrencySuggestions, setFirstCurrencySuggestions] = useState([]);
    const [secondCurrencySuggestions, setSecondCurrencySuggestions] = useState([]);
    const [networkSuggestions, setNetworkSuggestions] = useState([]);

    const dispatch = useDispatch();

    const allCurrencies = useMemo(() => {
        const set = new Set(DEFAULT_CURRENCIES);
        (gasFees || []).forEach(f => {
            if (f.firstCurrency) set.add(f.firstCurrency.toUpperCase());
            if (f.secondCurrency) set.add(f.secondCurrency.toUpperCase());
        });
        return [...set];
    }, [gasFees]);

    const allNetworks = useMemo(() => {
        const existingLabels = new Set(DEFAULT_NETWORKS.map(n => n.value));
        (gasFees || []).forEach(f => {
            if (f.network) existingLabels.add(f.network.toLowerCase());
        });
        return [...existingLabels].map(v => {
            const found = DEFAULT_NETWORKS.find(n => n.value === v);
            return found || { label: v, value: v };
        });
    }, [gasFees]);

    const filterCurrencies = (query, list) => {
        const q = query.toLowerCase();
        return list.filter(c => c.toLowerCase().includes(q));
    };

    const filterNetworks = (query) => {
        const q = query.toLowerCase();
        return allNetworks.filter(n => n.value.toLowerCase().includes(q) || n.label.toLowerCase().includes(q));
    };

    const onRowEditComplete = (e) => {
        dispatch(updateGasFees(e.data._id, e.newData, setLoading));
    };

    const textEditor = (options) => {
        return <InputText type="text" value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />;
    };

    const numberEditor = (options) => {
        return <InputText type="number" step="0.01" value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />;
    };

    const selectedExchangeRateTemplate = (options) => {
        return (
            <CustomDropdown
                options={["Market", "User"]}
                value={options.selectedExchangeRate}
                onChange={(e) => dispatch(updateGasFees(options._id, { selectedExchangeRate: e.value }, setLoading))}
            ></CustomDropdown>
        );
    };

    const rateBodyTemplate = (rowData) => {
        const rawRate = rowData.selectedExchangeRate === "Market" && rowData.liveMarketRate
            ? rowData.liveMarketRate
            : rowData.exchangePricePerUnit;
        const rate = adjustRate(rawRate, rowData.firstCurrency, rowData.secondCurrency);
        return `1 ${rowData.firstCurrency} = ${rate ? rate.toFixed(4) : "-"} ${rowData.secondCurrency}`;
    };

    const marketRateTemplate = (rowData) => {
        const live = rowData.liveMarketRate;
        const stored = rowData.marketRate;
        if (live) {
            const adj = adjustRate(live, rowData.firstCurrency, rowData.secondCurrency);
            const displayRate = `1 ${rowData.firstCurrency} = ${adj.toFixed(4)} ${rowData.secondCurrency}`;
            return <span title="Live rate from CoinGecko" style={{ color: "#08eefe" }}>{displayRate} <small style={{ opacity: 0.6, fontSize: "0.7em" }}>live</small></span>;
        }
        if (stored) {
            const adj = adjustRate(stored, rowData.firstCurrency, rowData.secondCurrency);
            const displayRate = `1 ${rowData.firstCurrency} = ${adj.toFixed(4)} ${rowData.secondCurrency}`;
            return <span>{displayRate}</span>;
        }
        return <span>-</span>;
    };

    const pairBodyTemplate = (rowData) => {
        return `${rowData.firstCurrency} → ${rowData.secondCurrency}`;
    };

    const handleAddFee = () => {
        const dup = (gasFees || []).find(
            f => f.firstCurrency?.toUpperCase() === newFee.firstCurrency?.toUpperCase() &&
                 f.secondCurrency?.toUpperCase() === newFee.secondCurrency?.toUpperCase() &&
                 f.network?.toLowerCase() === newFee.network?.toLowerCase()
        );
        if (dup) {
            dispatch({ type: types.SHOW_TOAST, payload: { severity: "error", summary: "Duplicate pair", detail: "This currency pair and network combination already exists." } });
            return;
        }
        api("post", Constants.END_POINT.GAS_FEES, newFee).then((res) => {
            if (res.success) {
                dispatch(getGasFeesCurrencyList(setLoading));
                setAddDialogVisible(false);
                setNewFee({
                    firstCurrency: "USDT",
                    secondCurrency: "CAD",
                    network: "tron",
                    exchangePricePerUnit: "",
                    fees: "5.00",
                    transactionFee: "2.50",
                    selectedExchangeRate: "User",
                    marketSpreadPercent: 0,
                });
            }
        });
    };

    const handleDeleteClick = (fee) => {
        setDeleteTarget(fee);
        setDeleteConfirmText("");
        setDeleteError("");
        setDeleteDialogVisible(true);
    };

    const handleDeleteConfirm = () => {
        if (deleteConfirmText !== "DELETE") {
            setDeleteError('Please type "DELETE" to confirm');
            return;
        }
        dispatch(deleteGasFees(deleteTarget._id, setLoading));
        setDeleteDialogVisible(false);
        setDeleteTarget(null);
        setDeleteConfirmText("");
        setDeleteError("");
    };

    const deleteBodyTemplate = (rowData) => {
        if (!editingRows[rowData._id]) return null;
        return (
            <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text"
                onClick={() => handleDeleteClick(rowData)}
                tooltip="Delete this pair"
                tooltipOptions={{ position: "left" }}
            />
        );
    };

    return (
        <>
            <TableCard title={heading} pagination={true}>
                <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "flex-end" }}>
                    <Button label="Add New Fee" icon="pi pi-plus" onClick={() => setAddDialogVisible(true)} style={{ fontSize: "0.875rem" }} />
                </div>
                <DataTable
                    value={gasFees}
                    selectionMode="checkbox"
                    showGridlines
                    loading={loading}
                    editMode="row"
                    editingRows={editingRows}
                    onRowEditChange={(e) => setEditingRows(e.data)}
                    onRowEditComplete={onRowEditComplete}
                    tableStyle={{ minWidth: "70rem" }}
                    emptyMessage="No gas fees currencies found."
                    sortField="firstCurrency"
                    sortOrder={1}
                    dataKey="_id"
                >
                    <Column body={(rowData, row) => row.rowIndex + 1} header="#" style={{ width: "3rem" }}></Column>
                    <Column body={pairBodyTemplate} header="Exchange Pair" style={{ width: "10rem" }} sortField="firstCurrency"></Column>
                    <Column header="Market Rate" style={{ width: "12rem" }} body={marketRateTemplate}></Column>
                    <Column
                        field="exchangePricePerUnit"
                        editor={(options) => numberEditor(options)}
                        header="User Rate"
                        body={rateBodyTemplate}
                        style={{ width: "12rem" }}
                    ></Column>
                    <Column
                        name="selectedExchangeRate"
                        field="selectedExchangeRate"
                        header="Selected Rate"
                        body={(rowData) => selectedExchangeRateTemplate(rowData)}
                        style={{ width: "8rem" }}
                    ></Column>
                    <Column
                        field="transactionFee"
                        header="Tx Fee (%)"
                        editor={(options) => numberEditor(options)}
                        style={{ width: "6rem" }}
                    ></Column>
                    <Column
                        field="marketSpreadPercent"
                        header="Spread %"
                        editor={(options) => numberEditor(options)}
                        style={{ width: "6rem" }}
                    ></Column>
                    <Column rowEditor headerStyle={{ width: "5%", minWidth: "5rem" }} bodyStyle={{ textAlign: "center" }}></Column>
                    <Column body={deleteBodyTemplate} headerStyle={{ width: "3rem" }} bodyStyle={{ textAlign: "center" }}></Column>
                </DataTable>
            </TableCard>

            <Dialog header="Add Gas Fee" visible={addDialogVisible} style={{ width: "450px" }} onHide={() => setAddDialogVisible(false)}>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>From Currency</label>
                            <AutoComplete
                                value={newFee.firstCurrency}
                                suggestions={firstCurrencySuggestions}
                                completeMethod={(e) => setFirstCurrencySuggestions(filterCurrencies(e.query, allCurrencies))}
                                onChange={(e) => setNewFee({ ...newFee, firstCurrency: e.value })}
                                dropdown
                                forceSelection={false}
                                style={{ width: "100%" }}
                                inputStyle={{ width: "100%" }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>To Currency</label>
                            <AutoComplete
                                value={newFee.secondCurrency}
                                suggestions={secondCurrencySuggestions}
                                completeMethod={(e) => setSecondCurrencySuggestions(filterCurrencies(e.query, allCurrencies))}
                                onChange={(e) => setNewFee({ ...newFee, secondCurrency: e.value })}
                                dropdown
                                forceSelection={false}
                                style={{ width: "100%" }}
                                inputStyle={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Network</label>
                        <AutoComplete
                            value={newFee.network}
                            suggestions={networkSuggestions}
                            completeMethod={(e) => setNetworkSuggestions(filterNetworks(e.query))}
                            onChange={(e) => setNewFee({ ...newFee, network: typeof e.value === 'object' ? e.value.value : e.value })}
                            field="label"
                            dropdown
                            forceSelection={false}
                            style={{ width: "100%" }}
                            inputStyle={{ width: "100%" }}
                        />
                    </div>
                    <div>
                        <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Exchange Rate (1 {newFee.firstCurrency} = ? {newFee.secondCurrency})</label>
                        <InputText type="number" step="0.0001" value={newFee.exchangePricePerUnit} onChange={(e) => setNewFee({ ...newFee, exchangePricePerUnit: e.target.value })} style={{ width: "100%" }} placeholder="e.g. 1.36" />
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Gas Fee</label>
                            <InputText type="number" step="0.01" value={newFee.fees} onChange={(e) => setNewFee({ ...newFee, fees: e.target.value })} style={{ width: "100%" }} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Transaction Fee (%)</label>
                            <InputText type="number" step="0.01" value={newFee.transactionFee} onChange={(e) => setNewFee({ ...newFee, transactionFee: e.target.value })} style={{ width: "100%" }} />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Market Spread (%)</label>
                        <InputText type="number" step="0.01" value={newFee.marketSpreadPercent} onChange={(e) => setNewFee({ ...newFee, marketSpreadPercent: e.target.value })} style={{ width: "100%" }} placeholder="e.g. 0.5" />
                    </div>
                    <Button label="Add Fee" icon="pi pi-check" onClick={handleAddFee} style={{ marginTop: "0.5rem" }} />
                </div>
            </Dialog>

            <Dialog
                header="Delete Gas Fee Pair"
                visible={deleteDialogVisible}
                style={{ width: "450px" }}
                onHide={() => { setDeleteDialogVisible(false); setDeleteTarget(null); setDeleteConfirmText(""); setDeleteError(""); }}
            >
                {deleteTarget && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <p>
                            Are you sure you want to delete the pair{" "}
                            <strong>{deleteTarget.firstCurrency} → {deleteTarget.secondCurrency}</strong>
                            {" "}on <strong>{deleteTarget.network}</strong>?
                        </p>
                        <p style={{ color: "#f39c12", margin: 0 }}>This action cannot be undone.</p>
                        <div>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Type "DELETE" to confirm</label>
                            <InputText
                                value={deleteConfirmText}
                                onChange={(e) => { setDeleteConfirmText(e.target.value); setDeleteError(""); }}
                                placeholder='Type "DELETE"'
                                style={{ width: "100%" }}
                                className={deleteError ? "p-invalid" : ""}
                            />
                            {deleteError && <small style={{ color: "#e74c3c" }}>{deleteError}</small>}
                        </div>
                        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                            <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => { setDeleteDialogVisible(false); setDeleteTarget(null); setDeleteConfirmText(""); setDeleteError(""); }} />
                            <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={handleDeleteConfirm} disabled={deleteConfirmText !== "DELETE"} />
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
}
